import React from "react";
import clsx from "clsx";
import {
  DialogTrigger,
  Modal as RACModal,
  Dialog,
  ModalOverlay,
  DialogRenderProps,
} from "react-aria-components";

import Button, { ButtonProps } from "../forms/button";
import { PalletLangContext } from "../../lang-provider";
import { dataAttributes, type DataAttributes } from "../../data-attributes";

import styles from "./modal.module.css";

/** The modal's contents: every slot takes a node or a render function. */
type ModalContent = {
  children:
    | ((dialogRenderProps: DialogRenderProps) => React.ReactNode)
    | React.ReactNode;
  header?:
    | ((dialogRenderProps: DialogRenderProps) => React.ReactNode)
    | React.ReactNode;
  footer?:
    | ((dialogRenderProps: DialogRenderProps) => React.ReactNode)
    | React.ReactNode;
  /**
   * The single answer to "can this be dismissed right now", and it governs
   * all three dismissal paths at once: outside press, escape, and the close
   * button — which stays rendered but goes disabled rather than disappearing,
   * so layout and focus order do not shift under the user mid-task.
   *
   * Measured need, not gold-plating: four outbound call sites pass
   * `!isPending`, switching dismissal off while a delete or a PIN reset is in
   * flight. Defaults to `true`, which is what `Modal` hard-coded before.
   */
  isDismissable?: boolean;
  /**
   * SR-301 phase 8 (B9). The plate is shrink-to-fit by default (unset,
   * matching every call site before this phase). Set this when the dialog's
   * own content resolves a `%`-based width against the plate — that
   * resolution is circular against a shrink-to-fit box, which is the
   * `selectors.tsx` defect this exists to fix. A named, token-backed union,
   * never a free number: pallet's `no-unknown-classes` accepts bracket
   * values like `w-[280px]`, so nothing else would stop a caller writing
   * one.
   */
  width?: "standard" | "wide";
};

/**
 * How the modal's open/closed state is driven, as a tagged union over
 * `control` -- the same per-arm idiom `Button` uses for `kind` and
 * `SidebarOverlay` uses for this exact question.
 *
 * `"trigger"` is the button-owned case: `Modal` renders the button itself as
 * `DialogTrigger`'s first child and react-aria owns open/closed.
 * `"external"` is the case a table row opens: no trigger is rendered at all
 * and the caller's own state is the only answer to "is it open", which is why
 * this arm renders no `DialogTrigger` -- a `DialogTrigger`'s internal state
 * would be a second, competing one. That is phase 5's as-built shape, copied
 * rather than re-derived.
 *
 * Each arm carries only the props that arm needs, so a controlled modal
 * cannot also be handed a trigger, and an `"external"` modal with no way to
 * close itself does not compile.
 */
type ModalControl =
  | { control: "trigger"; trigger: ButtonProps }
  | {
      control: "external";
      isOpen: boolean;
      onOpenChange: (isOpen: boolean) => void;
    };

export type ModalProps = ModalContent & ModalControl & DataAttributes;

function Modal(props: ModalProps) {
  const { header, children, footer, isDismissable = true, width } = props;
  const langContext = React.useContext(PalletLangContext);
  const closeLabel = langContext?.simpleTranslate("actions.close") ?? "Close";

  // The dialog takes its accessible name from whatever the caller rendered
  // into `header`. Under `control: "external"` there is no `DialogTrigger` to
  // fall back on for an `aria-labelledby`, so without this react-aria would
  // warn and the modal would go unnamed.
  const headerId = React.useId();

  // Lands on the `Dialog`, not the overlay: the overlay is a full-viewport
  // scrim, and a caller marking "the delete confirmation" means the box, not
  // the page behind it.
  const passthrough = dataAttributes(props);

  const panel = (
    <ModalOverlay
      isDismissable={isDismissable}
      isKeyboardDismissDisabled={!isDismissable}
      className={styles.modalOverlay}
      shouldCloseOnInteractOutside={() => {
        return isDismissable;
      }}
      {...(props.control === "external"
        ? { isOpen: props.isOpen, onOpenChange: props.onOpenChange }
        : {})}
    >
      <RACModal
        className={({ isEntering }) =>
          clsx(styles.modal, {
            [styles.loading]: isEntering,
            [styles.widthStandard]: width === "standard",
            [styles.widthWide]: width === "wide",
          })
        }
      >
        <Dialog
          {...passthrough}
          className={styles.dialog}
          aria-labelledby={headerId}
        >
          {(actions) => (
            <div>
              <div className={styles.header}>
                <div id={headerId}>
                  {typeof header === "function" ? header(actions) : header}
                </div>
                <Button
                  kind="icon-only"
                  label={closeLabel}
                  slot="close"
                  variant="tertiary"
                  icon="X"
                  isDisabled={!isDismissable}
                />
              </div>
              {typeof children === "function" ? children(actions) : children}
              {footer === undefined ? null : (
                <div className={styles.footer}>
                  {typeof footer === "function" ? footer(actions) : footer}
                </div>
              )}
            </div>
          )}
        </Dialog>
      </RACModal>
    </ModalOverlay>
  );

  if (props.control === "external") {
    return panel;
  }

  return (
    <DialogTrigger>
      <Button {...props.trigger} />
      {panel}
    </DialogTrigger>
  );
}

export default Modal;
